import { useEffect, useId, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiSearch } from "react-icons/fi";
import "./StexSelect.css";

const StexSelect = ({
  id: idProp,
  name,
  label,
  value = "",
  onChange,
  options = [],
  placeholder = "Select…",
  required = false,
  disabled = false,
  searchable = false,
}) => {
  const autoId = useId();
  const id = idProp || autoId;
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const term = query.trim().toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [options, query, searchable]);

  const selected = options.find((opt) => opt.value === value);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setHighlight(0);
    }
  }, [open]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectValue = (nextValue) => {
    onChange?.({
      target: { name, value: nextValue },
    });
    setOpen(false);
  };

  const onTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((o) => !o);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight(0);
    }
  };

  const onListKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, filtered.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    }
    if (e.key === "Enter" && filtered[highlight]) {
      e.preventDefault();
      selectValue(filtered[highlight].value);
    }
  };

  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector("[data-active='true']");
    active?.scrollIntoView({ block: "nearest" });
  }, [highlight, open, filtered]);

  return (
    <div
      className={`stex-select${open ? " stex-select--open" : ""}${disabled ? " stex-select--disabled" : ""}${searchable ? " stex-select--searchable" : ""}`}
      ref={rootRef}
    >
      {label && (
        <label className="stex-select__label" htmlFor={id}>
          {label}
        </label>
      )}
      <input type="hidden" name={name} value={value} required={required} tabIndex={-1} aria-hidden="true" />
      <button
        type="button"
        id={id}
        className="stex-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={selected ? "stex-select__value" : "stex-select__placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <FiChevronDown className="stex-select__chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="stex-select__content" role="listbox" onKeyDown={onListKeyDown}>
          {searchable && (
            <div className="stex-select__search">
              <FiSearch aria-hidden="true" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlight(0);
                }}
                placeholder="Search…"
                autoFocus
              />
            </div>
          )}
          <ul className="stex-select__list" ref={listRef}>
            {filtered.length === 0 ? (
              <li className="stex-select__empty">No results</li>
            ) : (
              filtered.map((opt, index) => {
                const isSelected = opt.value === value;
                const isActive = index === highlight;
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-active={isActive ? "true" : undefined}
                      className={`stex-select__item${isSelected ? " stex-select__item--selected" : ""}${
                        isActive ? " stex-select__item--active" : ""
                      }`}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => selectValue(opt.value)}
                    >
                      <span>{opt.label}</span>
                      {isSelected && <FiCheck className="stex-select__check" aria-hidden="true" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default StexSelect;
